/**
 * SG PreSet — Notice for Controllers 386AG(2)(a) (Individual)
 *
 * Template variables:
 *   notice_date, notice_recipient_name, company_name, reply_to_address, notice_signer_name,
 *   controller_full_name, controller_aliases, controller_residential_address,
 *   controller_email, controller_contact_no, controller_nationality,
 *   controller_identification_no, controller_date_of_birth, controller_became_date
 */

import { buildNoticeIndividualContactDataFromPreset, PRESET_MANUAL_CONTROLLER_CONTACT_CD } from './adaptControllerEfilingFormData';
import { nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';

export interface NoticeIndividualFormModel {
  noticeDate: string;
  noticeRecipientName: string;
  companyName: string;
  /** ACRA Entity UEN (회사 등록번호) — E-Filing EntityUEN 용 */
  companyRegistrationNo: string;
  replyToAddress: string;
  noticeSignerName: string;
  controllerFullName: string;
  controllerAliases: string;
  controllerResidentialAddress: string;
  controllerAlternateAddress: string;
  controllerEmail: string;
  controllerContactNo: string;
  controllerIdTypeCd: string;
  controllerMobileCountryCd: string;
  controllerMobileAreaCode: string;
  controllerNationality: string;
  controllerIdentificationNo: string;
  controllerDateOfBirth: string;
  controllerBecameDate: string;
  contactCd: string;
  contactData: Record<string, any> | null;
}

export function createEmptyNoticeIndividualForm(): NoticeIndividualFormModel {
  return {
    noticeDate: '',
    noticeRecipientName: '',
    companyName: '',
    companyRegistrationNo: '',
    replyToAddress: '',
    noticeSignerName: '',
    controllerFullName: '',
    controllerAliases: '',
    controllerResidentialAddress: '',
    controllerAlternateAddress: '',
    controllerEmail: '',
    controllerContactNo: '',
    controllerIdTypeCd: '',
    controllerMobileCountryCd: '',
    controllerMobileAreaCode: '',
    controllerNationality: '',
    controllerIdentificationNo: '',
    controllerDateOfBirth: '',
    controllerBecameDate: '',
    contactCd: '',
    contactData: null,
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** LETTAGA 내부 entity PK(GUID) — ACRA EntityUEN 으로 사용 금지 */
function looksLikeUuid(s: string): boolean {
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
}

export function resolveNoticeIndividualEntityUen(
  model: Pick<NoticeIndividualFormModel, 'companyRegistrationNo'>,
  entityCd = '',
): string {
  const companyUen = trim(model.companyRegistrationNo);
  const ec = trim(entityCd);
  if (companyUen) return companyUen;
  if (ec && !looksLikeUuid(ec)) return ec;
  return '';
}

export function toNoticeIndividualTemplateVariables(
  model: NoticeIndividualFormModel,
): Record<string, string> {
  const controllerFullName = trim(model.controllerFullName);
  const noticeRecipientName =
    trim(model.noticeRecipientName) || controllerFullName;
  const entityUen = trim(model.companyRegistrationNo);

  return {
    notice_date: trim(model.noticeDate),
    notice_recipient_name: noticeRecipientName,
    company_name: trim(model.companyName),
    company_registration_no: entityUen,
    entity_uen: entityUen,
    entityUEN: entityUen,
    EntityUEN: entityUen,
    entityRegNo: entityUen,
    reply_to_address: trim(model.replyToAddress),
    notice_signer_name: trim(model.noticeSignerName),
    controller_full_name: controllerFullName,
    controller_aliases: trim(model.controllerAliases),
    controller_residential_address: trim(model.controllerResidentialAddress),
    controller_alternate_address: trim(model.controllerAlternateAddress),
    controller_email: trim(model.controllerEmail),
    controller_contact_no: trim(model.controllerContactNo),
    controller_id_type_cd: trim(model.controllerIdTypeCd),
    controller_mobile_country_cd: trim(model.controllerMobileCountryCd),
    controller_mobile_area_code: trim(model.controllerMobileAreaCode),
    controller_nationality: nationalityLabelForOutput(trim(model.controllerNationality)),
    controller_identification_no: trim(model.controllerIdentificationNo),
    controller_date_of_birth: trim(model.controllerDateOfBirth),
    controller_became_date: trim(model.controllerBecameDate),
    contact_cd: trim(model.contactCd),
    contactCd: trim(model.contactCd),
    signers: '',
    Signers: '',
  };
}

export function syncNoticeIndividualManualContactData(model: NoticeIndividualFormModel): void {
  const vars = toNoticeIndividualTemplateVariables(model);
  model.contactData = buildNoticeIndividualContactDataFromPreset({
    ...vars,
    controllerIdTypeCd: model.controllerIdTypeCd,
    controllerMobileCountryCd: model.controllerMobileCountryCd,
    controllerMobileAreaCode: model.controllerMobileAreaCode,
    controllerAlternateAddress: model.controllerAlternateAddress,
  }) as Record<string, any>;
}

export function buildNoticeIndividualTaskFormData(
  model: NoticeIndividualFormModel,
  entityCd: string,
): Record<string, unknown> {
  syncNoticeIndividualManualContactData(model);
  const ec = trim(entityCd);
  const entityUen = resolveNoticeIndividualEntityUen(model, ec);
  const vars = toNoticeIndividualTemplateVariables(model);
  const contactCd = trim(model.contactCd);
  const contactData = model.contactData ?? undefined;
  const fullName = trim(model.controllerFullName);

  return {
    ...vars,
    entityCd: ec,
    entity: ec,
    contactCd,
    contact_cd: contactCd,
    controllerCd: contactCd,
    controllerName: contactCd,
    companyRegistrationNo: entityUen,
    entityRegNo: entityUen,
    entityName: { value: trim(model.companyName) },
    categoryCd: 'B09002',
    categoryCode: 'B09002',
    controllerIdTypeCd: trim(model.controllerIdTypeCd),
    controllerMobileCountryCd: trim(model.controllerMobileCountryCd),
    controllerMobileAreaCode: trim(model.controllerMobileAreaCode),
    controllerAlternateAddress: trim(model.controllerAlternateAddress),
    ...(contactData ? { contactData } : {}),
    ...(fullName
      ? {
        presetControllerContactOption: {
          title: fullName,
          value: contactCd || PRESET_MANUAL_CONTROLLER_CONTACT_CD,
        },
      }
      : {}),
  };
}

export function isNoticeIndividualFormComplete(model: NoticeIndividualFormModel): boolean {
  return [
    model.noticeDate,
    model.companyName,
    model.replyToAddress,
    model.noticeSignerName,
    model.controllerFullName,
    model.controllerResidentialAddress,
    model.controllerEmail,
    model.controllerContactNo,
    model.controllerMobileCountryCd,
    model.controllerNationality,
    model.controllerIdentificationNo,
    model.controllerDateOfBirth,
    model.controllerBecameDate,
  ].every((v) => trim(v).length > 0);
}

/** Entity snapshot / chat selection → company + reply address */
export function applyEntityBasicsToNoticeIndividualForm(
  model: NoticeIndividualFormModel,
  ent: Record<string, unknown> | null | undefined,
): void {
  if (!ent) return;
  const name = trim(ent.companyName ?? ent.entityName ?? ent.ENTITY_NAME);
  if (name) model.companyName = name;
  const reg = trim(
    ent.registrationNo ?? ent.regNo ?? ent.REG_NO ?? ent.companyRegistrationNo,
  );
  if (reg) model.companyRegistrationNo = reg;
}
